import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MdwSelect } from '@spaces-ui/meadow/components/select';
import { MdwTabGroup, TabItem } from '@spaces-ui/meadow/components/tab-group';
import { AppearanceMode, ThemeIntrospectionService, ThemeService } from '@spaces-ui/theme';

/** One picker entry: a theme family and its display name. */
interface FamilyOption {
  family: string;
  name: string;
}

@Component({
  imports: [MdwSelect, MdwTabGroup, FormsModule],
  selector: 'app-top-sidebar',
  templateUrl: './app-top-sidebar.html',
  styleUrl: './app-top-sidebar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopSidebar {
  private themeService = inject(ThemeService);
  private introspection = inject(ThemeIntrospectionService);

  /** Current family + mode, exposed for the template. */
  protected readonly family = this.themeService.family;
  protected readonly mode = this.themeService.mode;

  /**
   * One option per discovered family (light/dark variants collapsed into a single
   * entry). Reads the introspection signal so the list fills in once the global
   * stylesheet finishes parsing.
   */
  protected readonly familyOptions = computed<FamilyOption[]>(() => {
    const byFamily = new Map<string, string>();
    for (const theme of this.introspection.themes()) {
      if (!byFamily.has(theme.family)) {
        byFamily.set(theme.family, theme.name);
      }
    }
    return Array.from(byFamily, ([family, name]) => ({ family, name }));
  });

  protected readonly modeTabs: TabItem[] = [
    { label: 'Light', alias: 'light' },
    { label: 'Dark', alias: 'dark' },
  ];

  protected selectFamily(family: string): void {
    this.themeService.setFamily(family);
  }

  protected selectMode(alias: string): void {
    this.themeService.setMode(alias as AppearanceMode);
  }
}
